// @ts-nocheck
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ContentWrapper } from "@insite/content-library/Widgets/Basic/Button";
import Button from "@insite/mobius/Button";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { widgetWrapperStyles } from "@store/Library/Components/Toolkit/WidgetWrapper";
import getBooleanProperty from "@toolkit/Framework/Common/Utilities/getBooleanProperty";
import * as cssLinter from "css";
import React from "react";
import { useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        isSignedIn: state.context.session.userName && !getCurrentUserIsGuest(state),
        productState: getProductState(state, state.pages.productDetails.selectedProductId).value,
        canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
        isAuthenticated: state.context.session.isAuthenticated,
    };
};

type Props = WidgetProps & OwnProps & ReturnType<typeof mapStateToProps>;

const enum fields {
    variant = "variant",
    label = "label",
    alignment = "alignment",
    customCSS = "customCSS",
    hsformLink = "hsformLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.variant]: "primary" | "secondary" | "tertiary";
        [fields.label]: string;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        [fields.hsformLink]: string;
    };
}

const wrapperStyles: InjectableCss = widgetWrapperStyles;

const RequestQuoteButton = ({ fields, productState }: Props) => {
    const { product } = useContext(ProductContext);
    const productProperties = product.properties;
    const canRequestQuote = getBooleanProperty(productProperties?.canRequestQuote);
    const isVariantParent = productState?.isVariantParent;

    const requestQuoteHandler = () => {
        window.open(fields.hsformLink, "_blank");
    };

    const styles = useMergeStyles("button", { wrapperStyles });

    const customCssWrapper = {
        css: css`
            ${styles?.wrapperStyles?.css}
            ${fields.customCSS}
        `,
    };

    if (!canRequestQuote || (isVariantParent && product.variantTraits?.length)) {
        return null;
    }

    return (
        <ContentWrapper alignment={fields.alignment} {...customCssWrapper} className="wrapper">
            <Button variant={fields.variant} onClick={requestQuoteHandler} className="button">
                {fields.label}
            </Button>
        </ContentWrapper>
    );
};

const defaultCustomCss = `& {
}

.button {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RequestQuoteButton),
    definition: {
        group: "Common",
        icon: "rectangle-wide",
        displayName: "Request Quote Button",
        allowedContexts: ["ProductDetailsPage", "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.variant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Request a Quote or Info Today!",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.hsformLink,
                displayName: "Hubspot Form Link",
                editorTemplate: "TextField",
                defaultValue: "https://share.hsforms.com/1_tNNJXELTfKhqGZPKuoHnw4puyh",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                name: fields.alignment,
                displayName: "Button Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "primary",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
